import { z } from 'zod';

/**
 * De openbare websitefeed (WEB-1, ADR-0097): de berichten die een organisatie zelf op haar eigen
 * website wil tonen, zonder aanmelding op te halen.
 *
 * Twee vormen voor hetzelfde lijstje:
 *
 * - **De nieuwe vorm** (`GET /public/feeds/:tenantCode`): wat een nieuwe website hoort te lezen.
 * - **De oude vorm** (`GET /public/feeds/:tenantCode/legacy`): veld voor veld wat het vorige
 *   platform op `/api/v1/public/<orgId>` gaf. Een club die die feed al op haar website had, past
 *   alleen de URL aan; het script op de website blijft ongewijzigd.
 *
 * Wat er níét in staat, is even belangrijk: geen ontvangers, geen leescijfers, geen reacties, geen
 * doelgroep. De naam van de **afzender** wel — die stond ook in de oude feed, en een bericht op een
 * website zonder afzender is een anoniem bericht (ADR-0014 gaat over ontvangers, niet over afzenders).
 */

/** Hoeveel berichten de feed standaard geeft, en hoeveel ten hoogste. */
export const WEBSITE_FEED_DEFAULT_LIMIT = 10;
export const WEBSITE_FEED_MAX_LIMIT = 50;

/** Hoe lang een browser of proxy een antwoord van de feed mag bewaren, in seconden. */
export const WEBSITE_FEED_CACHE_SECONDS = 60;

/**
 * Eén bestand bij een bericht.
 *
 * `url` is een **blijvend** adres op de API, geen ondertekende opslag-URL: een ondertekende URL is
 * zestig seconden geldig, en een website die een pdf-link pas na tien minuten laat aanklikken, zou
 * dan een foutpagina openen. Het blijvende adres stuurt bij elke aanvraag door naar een verse
 * ondertekende URL. De bestandsnaam staat achteraan in het pad, zodat een script dat op `.pdf`
 * controleert, blijft werken.
 */
export const websiteFeedMediaSchema = z.object({
  url: z.string().url(),
  kind: z.enum(['image', 'video', 'document']),
  mimeType: z.string(),
  fileName: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type WebsiteFeedMedia = z.infer<typeof websiteFeedMediaSchema>;

export const websiteFeedPostSchema = z.object({
  id: z.guid(),
  title: z.string(),
  /** De HTML van de server, uit de witte lijst van `@up2d8/rich-text` (ADR-0037); nooit invoer. */
  html: z.string(),
  /** De eerste regels als platte tekst, voor een overzicht met alleen een aanhef. */
  excerpt: z.string(),
  publishedAt: z.string().datetime(),
  /** De afzender: de naam van de schrijver, of het afzenderlabel ("Namens het bestuur"). */
  author: z.string().nullable(),
  media: z.array(websiteFeedMediaSchema),
});
export type WebsiteFeedPost = z.infer<typeof websiteFeedPostSchema>;

export const websiteFeedSchema = z.object({
  organization: z.object({
    name: z.string(),
    /** Het logo, als blijvend adres (zie `websiteFeedMediaSchema`); `null` zonder logo. */
    logoUrl: z.string().url().nullable(),
  }),
  items: z.array(websiteFeedPostSchema),
  generatedAt: z.string().datetime(),
});
export type WebsiteFeed = z.infer<typeof websiteFeedSchema>;

/**
 * De vorm van het vorige platform, letterlijk.
 *
 * `content` is de HTML met de titel vooraan in een `<p><strong>`: het oude platform kende geen
 * titels, dus een website die alleen `content` toont, zou de titel anders kwijtraken. `medias` zijn
 * absolute URL's, zoals voorheen. `creator.name` valt terug op de naam van de organisatie als een
 * bericht geen afzender heeft (een import); de oude feed had altijd een naam.
 */
export const legacyWebsiteFeedSchema = z.object({
  statusCode: z.literal(200),
  message: z.string(),
  payload: z.object({
    posts: z.array(
      z.object({
        _id: z.string(),
        content: z.string(),
        createdAt: z.string().datetime(),
        medias: z.array(z.string()),
        creator: z.object({ name: z.string() }),
      }),
    ),
    organization: z.object({
      name: z.string(),
      avatar: z.string().nullable(),
    }),
  }),
});
export type LegacyWebsiteFeed = z.infer<typeof legacyWebsiteFeedSchema>;

/**
 * De instelling in het beheer: staat de feed open, en op welke adressen.
 *
 * De adressen komen van de server en worden niet in de client samengesteld: alleen de server weet
 * op welk domein de API voor de buitenwereld bereikbaar is.
 */
export const websiteFeedSettingsSchema = z.object({
  enabled: z.boolean(),
  tenantCode: z.string(),
  feedUrl: z.string().url(),
  legacyUrl: z.string().url(),
});
export type WebsiteFeedSettings = z.infer<typeof websiteFeedSettingsSchema>;

export const updateWebsiteFeedSettingsRequestSchema = z.object({
  enabled: z.boolean(),
});
export type UpdateWebsiteFeedSettingsRequest = z.infer<
  typeof updateWebsiteFeedSettingsRequestSchema
>;
